import { ChangeDetectionStrategy, Component, ViewChild, inject, input } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Doctor, DoctorRepositoryService, PatientDoctor } from '@xmedical/core';
import { AppMatAutocompleteComponent } from '@xmedical/ui';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DoctorAddCardComponent } from '../../doctor-add-card/doctor-add-card.component';
import { DoctorEditCardComponent } from '../../doctor-edit-card/doctor-edit-card.component';
import { DoctorFormShape, makeDoctorFormGroup } from '../patient-form.service';

export type DoctorKind = 'family' | 'referring';

// `Doctor` minus the `id` field — what the form stores.
function toPatientDoctor(doctor: Doctor): PatientDoctor {
  const { id, ...rest } = doctor;
  return rest;
}

@Component({
  selector: 'app-patient-doctors-fieldset',
  templateUrl: './patient-doctors-fieldset.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientDoctorsFieldsetComponent {
  private doctorsRepository = inject(DoctorRepositoryService);
  private matDialog = inject(MatDialog);

  readonly array = input.required<FormArray<FormGroup<DoctorFormShape>>>();
  readonly kind = input.required<DoctorKind>();
  readonly heading = input.required<string>();
  readonly emptyMessage = input.required<string>();

  @ViewChild('autocomplete') private autocomplete?: AppMatAutocompleteComponent;

  readonly searchDoctors = (query: string): Observable<Doctor[]> => {
    return this.doctorsRepository.query(query.toLowerCase()).pipe(map(response => response.map(d => d)));
  };

  doctorSelected(doctor: Doctor): void {
    this.array().push(makeDoctorFormGroup(toPatientDoctor(doctor)));
  }

  remove(index: number): void {
    this.array().removeAt(index);
  }

  addNewDoctor(): void {
    const ref = this.matDialog.open(DoctorAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
    });
    ref.afterClosed().subscribe(doctor => {
      if (doctor) {
        this.autocomplete?.search(doctor.name);
      }
    });
  }

  edit(index: number): void {
    const existing = this.array().at(index).value as PatientDoctor;
    const ref = this.matDialog.open(DoctorEditCardComponent, {
      data: existing,
      panelClass: 'mat-confirmation-dialog-responsive',
    });
    ref.afterClosed().subscribe(updated => {
      if (updated) {
        this.array().at(index).patchValue(updated);
      }
    });
  }
}
